import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Apple, ShieldAlert } from 'lucide-react';
import Button from '../../ui/Button';
import { cn } from '../../../utils/cn';
import type { PickQuarantineOrder } from '../../../types/nurseryQuarantine';

/**
 * Tells a picker that the customer they are picking for has an order going
 * interstate under a quarantine protocol, and where the picked order is to be
 * taken to be treated (Rob, 2026-10-14). Shown once as a dialog when the
 * customer is opened, and stays on the page as a banner; once the customer is
 * picked it says to take the order there now. Until it is treated, it also
 * says to strip the fruit as the trees are picked.
 */

const SEEN_KEY = 'nursery_pick_quarantine_seen';

function seenBefore(key: string): boolean {
  try { return (sessionStorage.getItem(SEEN_KEY) ?? '').split('|').includes(key); } catch { return false; }
}

function markSeen(key: string) {
  try {
    const seen = (sessionStorage.getItem(SEEN_KEY) ?? '').split('|').filter(Boolean);
    sessionStorage.setItem(SEEN_KEY, [...seen.slice(-50), key].join('|'));
  } catch { /* the dialog shows again next time, nothing else */ }
}

/** Where the order goes once picked: the area its treatment was made at, else the protocol's areas. */
function whereText(o: PickQuarantineOrder, t: TFunction): string {
  if (o.treated) return t('alreadyTreated', { order: o.order_number });
  const areas = o.treatment_areas.length ? o.treatment_areas : o.areas.map((a) => a.name);
  if (areas.length === 1) return t('takeTo', { order: o.order_number, area: areas[0] });
  if (areas.length > 1) return t('takeToOneOf', { order: o.order_number, areas: areas.join(', ') });
  return t('keepApart', { order: o.order_number });
}

export function QuarantinePickNotice({ orders, seenKey, done }: {
  /** The customer's orders going under a protocol. Nothing shows when empty. */
  orders: PickQuarantineOrder[];
  /** Load and customer: the dialog shows once per customer per browser session. */
  seenKey: string;
  /** Everything for the customer is picked: take it to be treated now. */
  done: boolean;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'QuarantinePickNotice' });
  const [dismissed, setDismissed] = useState<string | null>(null);
  if (!orders.length) return null;
  const allTreated = orders.every((o) => o.treated);
  const dialogOpen = !allTreated && !done && dismissed !== seenKey && !seenBefore(seenKey);
  const close = () => { markSeen(seenKey); setDismissed(seenKey); };
  const lines = orders.map((o) => ({
    key: o.order_id,
    goes: t('goesUnder', { order: o.order_number, state: o.state, code: o.code }),
    where: whereText(o, t),
  }));

  const removeFruit = (
    <p className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 font-semibold text-red-900">
      <Apple className="h-5 w-5 flex-shrink-0" />
      <span className="min-w-0 break-words">{t('removeFruit')}</span>
    </p>
  );

  const body = (
    <ul className="space-y-2">
      {lines.map((l) => (
        <li key={l.key} className="break-words">
          <span className="font-medium">{l.goes}</span> {l.where}
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <div
        role="status"
        className={cn('rounded-xl p-4 text-sm space-y-2 border', done && !allTreated
          ? 'bg-amber-100 border-amber-300 text-amber-950'
          : 'bg-amber-50 border-amber-200 text-amber-900')}
      >
        <p className="flex items-start gap-2 font-semibold">
          <ShieldAlert className="h-5 w-5 flex-shrink-0" />
          <span className="min-w-0 break-words">
            {allTreated ? t('titleTreated') : done ? t('titleDone') : t('title')}
          </span>
        </p>
        {!allTreated && removeFruit}
        {body}
        {!allTreated && <p className="text-xs break-words">{t('why')}</p>}
      </div>

      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="quarantine-pick-title">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto">
            <h2 id="quarantine-pick-title" className="flex items-start gap-2 text-base font-semibold text-amber-900">
              <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span className="min-w-0 break-words">{t('title')}</span>
            </h2>
            <div className="text-sm">{removeFruit}</div>
            <div className="text-sm text-neutral-800">{body}</div>
            <p className="text-xs text-neutral-600 break-words">{t('why')}</p>
            <div className="flex justify-end">
              <Button onClick={close}>{t('gotIt')}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QuarantinePickNotice;
